import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/format';
import type {
  DdsHistoryEvent,
  TransitDetail,
} from '@/types/core/transit/transit';
import { getDdsErrorMessage } from './dds-error-message';
import { DdsNumber } from './dds-number';
import { DdsStatusBadge } from './dds-status-badge';

function DdsDate({ value }: { value: string }) {
  const { i18n } = useTranslation('transits');

  return (
    <time className='text-muted-foreground text-xs' dateTime={value}>
      {formatDate(value, {
        locale: i18n.language,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })}
    </time>
  );
}

function DdsEvents({ events }: { events: DdsHistoryEvent[] }) {
  const { t } = useTranslation('transits');

  const visibleEvents = events
    .filter((event) => event.type !== 'preparation')
    .sort(
      (a, b) =>
        Date.parse(b.occurredAt) - Date.parse(a.occurredAt) ||
        b.id.localeCompare(a.id),
    );

  if (visibleEvents.length === 0) {
    return (
      <p className='mt-2 text-muted-foreground text-xs'>
        {t('ddsNoHistoryEvents')}
      </p>
    );
  }

  return (
    <ol className='mt-2 flex flex-col gap-3'>
      {visibleEvents.map((event) => (
        <li className='text-sm' key={event.id}>
          <div className='flex flex-wrap items-center gap-2'>
            <span>
              {t(
                event.type === 'reread' &&
                  event.consultationTrigger === 'manual'
                  ? 'ddsManualConsultation'
                  : `ddsHistoryEvents.${event.type}`,
              )}
            </span>
            <DdsDate value={event.occurredAt} />
            {event.consultationOutcome && (
              <span>
                {t(`ddsConsultationOutcome.${event.consultationOutcome}`)}
              </span>
            )}
            {event.previousStatus && (
              <>
                <DdsStatusBadge status={event.previousStatus} />
                <span>{t('ddsStatusTransitionTo')}</span>
              </>
            )}
            {event.status && <DdsStatusBadge status={event.status} />}
          </div>
          {event.error && (
            <p className='mt-1 whitespace-pre-wrap break-words text-destructive'>
              {getDdsErrorMessage(event.error, t)}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

export function TransitDdsHistory({
  tracking,
}: {
  tracking: TransitDetail['purchaseOrders'][number];
}) {
  const { t } = useTranslation('transits');

  return (
    <>
      {tracking.ddsLastAccepted && (
        <div className='mt-2 rounded-md border p-3'>
          <p className='mb-2 font-medium text-sm'>{t('ddsLastAccepted')}</p>
          <dl className='grid gap-2 sm:grid-cols-2'>
            {tracking.ddsLastAccepted.referenceNumber && (
              <DdsNumber
                label={t('ddsReferenceNumber')}
                value={tracking.ddsLastAccepted.referenceNumber}
              />
            )}
            {tracking.ddsLastAccepted.verificationNumber && (
              <DdsNumber
                label={t('ddsVerificationNumber')}
                value={tracking.ddsLastAccepted.verificationNumber}
              />
            )}
          </dl>
        </div>
      )}
      {tracking.ddsHistory.length > 0 && (
        <details className='mt-2 rounded-md border p-3'>
          <summary className='cursor-pointer font-medium text-sm'>
            {t('ddsHistory', { count: tracking.ddsHistory.length })}
          </summary>
          <ol className='mt-3 flex flex-col gap-3'>
            {tracking.ddsHistory.map((declaration) => (
              <li className='rounded-md border p-3' key={declaration.id}>
                {tracking.ddsHistory.length > 1 && (
                  <p className='mb-2 font-medium text-sm'>
                    {t(
                      declaration.isCurrent
                        ? 'ddsCurrentDeclaration'
                        : 'ddsReplacedDeclaration',
                    )}
                  </p>
                )}
                {declaration.rejectionReason && (
                  <p className='whitespace-pre-wrap break-words text-destructive text-sm'>
                    {t('ddsRejectionReason')} {declaration.rejectionReason}
                  </p>
                )}
                {declaration.events ? (
                  <DdsEvents events={declaration.events} />
                ) : declaration.submissions.length > 0 ? (
                  <ul className='mt-2 flex flex-col gap-2'>
                    {declaration.submissions.map((submission) => (
                      <li
                        className='flex flex-wrap items-center gap-2 text-sm'
                        key={submission.id}
                      >
                        <span>
                          {t(
                            submission.operation === 'amendDds'
                              ? 'ddsAmendment'
                              : 'ddsSubmission',
                          )}
                        </span>
                        <DdsDate value={submission.occurredAt} />
                        <DdsStatusBadge
                          label={t(
                            submission.operation === 'amendDds' &&
                              submission.status === 'error'
                              ? 'ddsAmendFailedStatus'
                              : `ddsSubmissionStatus.${submission.status}`,
                          )}
                          status={submission.status}
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='mt-2 text-muted-foreground text-xs'>
                    {t('ddsNoSubmission')}
                  </p>
                )}
                <p className='mt-3 flex flex-wrap items-center gap-2 text-sm'>
                  <span>{t('ddsCreatedAt')}</span>
                  <DdsDate value={declaration.createdAt} />
                </p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </>
  );
}
